/**
 * Wer darf in VARIO angelegt werden? Zwei Bedingungen, beide mit Vorgeschichte.
 *
 * 1. **Noch nicht zugeordnet.** Geprueft wird `isMapped`, nicht `varioArticleNumber`: der
 *    Variantenhauptartikel hat nie eine eigene Nummer und wuerde sonst mitgeschickt, obwohl seine
 *    Kinder laengst verknuepft sind.
 * 2. **Im Gambio-Shop aktiv.** Betreiber-Vorgabe 25.09.2026: „Wir wollen nur aktive Artikel
 *    uebernehmen." Anlass waren zwei Artikel, die es trotzdem nach VARIO geschafft hatten -
 *    `50-126` („Spiegelei-Pfanne") und `22927` („Design Pfannenwender - Kopie"), beide im Shop
 *    inaktiv. Die Liste liess ihre Zeilen auswaehlen, und der Seed legte sie an.
 *
 * Massgeblich ist `effectiveIsActive`, nicht `isActive`: Varianten-Kinder tragen in Gambio kein
 * eigenes `isActive`, in der Sicht steht dort `null`, und sie erben den Wert ihres Vaters (siehe
 * `products-filter.ts`, gleicher Stolperstein).
 *
 * Aussortiert wird nur, was NACHWEISLICH inaktiv ist (`=== false`). Ein unbekannter Status
 * (`null` ohne Vater) blockiert die Anlage nicht - der Anwender hat die Zeile bewusst markiert,
 * und wir wollen ihm nichts verweigern, was wir nicht belegen koennen. Der Weg „alle Treffer"
 * ist strenger, weil dort die Datenbank filtert (`effective_is_active is true`); das ist der
 * Preis dafuer, dass sich ueber alle Treffer nicht zeilenweise aussortieren laesst.
 */

export interface SeedKandidat {
  /** true = die Zeile haengt bereits an einem VARIO-Artikel (oder ihre Gruppe tut es). */
  isMapped?: boolean;
  /** Aktiv-Status der Zeile, bei Varianten-Kindern der des Vaters. */
  effectiveIsActive: boolean | null;
}

export interface SeedAuswahl<T> {
  /** Zeilen, fuer die angelegt werden soll. */
  anzulegen: T[];
  /** Wie viele nicht zugeordnete Zeilen wegen „inaktiv" aussortiert wurden. */
  uebersprungenInaktiv: number;
}

/** Teilt eine Auswahl in „wird angelegt" und „inaktiv, deshalb nicht". */
export function seedKandidaten<T extends SeedKandidat>(zeilen: readonly T[]): SeedAuswahl<T> {
  const anzulegen: T[] = [];
  let uebersprungenInaktiv = 0;
  for (const z of zeilen) {
    if (z.isMapped) continue;
    if (z.effectiveIsActive === false) { uebersprungenInaktiv += 1; continue; }
    anzulegen.push(z);
  }
  return { anzulegen, uebersprungenInaktiv };
}

/**
 * Der Hinweis fuer den Anwender, wenn Zeilen wegen „inaktiv" wegfallen. `null`, wenn es nichts zu
 * sagen gibt. Eigene Funktion, damit der Text testbar ist und nicht im Klick-Handler versauert.
 */
export function inaktivHinweis(uebersprungenInaktiv: number, anzulegen: number): string | null {
  if (uebersprungenInaktiv === 0) return null;
  if (anzulegen === 0) {
    const satz = uebersprungenInaktiv === 1 ? 'markierter Artikel ist' : 'markierte Artikel sind';
    return `Nichts anzulegen: ${uebersprungenInaktiv} ${satz} im Gambio-Shop inaktiv, `
      + 'und nur aktive werden übernommen.';
  }
  return `${uebersprungenInaktiv} inaktive Artikel übersprungen - es werden nur aktive übernommen.`;
}
